import { ReactElement, useMemo, useState, type ReactNode } from 'react';
import {
  Flex,
  Stack,
  Button,
  useComputedColorScheme,
  Group,
  SimpleGrid,
  Text,
  ThemeIcon,
  Accordion,
} from '@mantine/core';
import {
  IconBuildings,
  IconCirclePlus,
  IconCreditCard,
  IconDeviceDesktopAnalytics,
  IconReceipt2,
} from '@tabler/icons-react';
import { buttonTextColor } from 'utils/buttonUtils';
import { validateSiteContactValues } from '../../../../validation/site';
import { provincesEnum, type Province } from '@billing/validation/payor';
import type { MRT_Row, MRT_TableInstance } from 'mantine-react-table';
import type { Site } from 'interfaces/site';
import { SiteValidationProvider } from './SiteValidationContext';

type SiteModalProps = {
  row: MRT_Row<Site>;
  table: MRT_TableInstance<Site>;
  internalEditComponents?: ReactNode[];
  type: 'create' | 'edit';
};

const getRowPrefix = (type: 'create' | 'edit', rowId?: string) =>
  type === 'edit' ? rowId : 'mrt-row-create';

const ModalSection = ({
  value,
  title,
  description,
  icon,
  children,
  columns = 2,
}: {
  value: string;
  title: string;
  description?: string;
  icon: ReactNode;
  children: ReactNode[];
  columns?: number;
}) => {
  const fields = children.filter(Boolean);
  if (!fields.length) return null;

  return (
    <Accordion.Item value={value}>
      <Accordion.Control>
        <Group gap="sm" align="flex-start">
          <ThemeIcon variant="light" size="lg" radius="md">
            {icon}
          </ThemeIcon>
          <div>
            <Text fw={600} size="lg">
              {title}
            </Text>
            {description ? (
              <Text size="sm" c="dimmed">
                {description}
              </Text>
            ) : null}
          </div>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <SimpleGrid
          cols={{ base: 1, md: columns }}
          spacing="lg"
          verticalSpacing="lg"
        >
          {fields}
        </SimpleGrid>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export const SiteModal = ({
  row,
  table,
  internalEditComponents = [],
  type,
}: SiteModalProps) => {
  const computedColorScheme = useComputedColorScheme('dark');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [province, setProvince] = useState<Province | null>(() => {
    const result = provincesEnum.safeParse(row.original.province);

    return result.success ? result.data : null;
  });

  const onSave = async () => {
    const validationResult = validateSiteContactValues({
      postalCode: row._valuesCache?.postalCode ?? row.original?.postalCode,
      fax: row._valuesCache?.fax ?? row.original?.fax ?? '',
    });

    if (!validationResult.isValid) {
      setShowValidationErrors(true);
      return;
    }

    setShowValidationErrors(false);
    if (type === 'edit') {
      await table.options.onEditingRowSave?.({
        exitEditingMode: () => table.setEditingRow(null),
        row,
        table,
        values: row._valuesCache,
      });
      return;
    }

    await table.options.onCreatingRowSave?.({
      exitCreatingMode: () => table.setCreatingRow(null),
      row,
      table,
      values: row._valuesCache,
    });
  };

  const editComponentsMap = useMemo(() => {
    const map: Record<string, ReactNode> = {};
    internalEditComponents.forEach((comp: ReactElement) => {
      if (comp.key) map[comp.key as string] = comp;
    });
    return map;
  }, [internalEditComponents]);

  const rowPrefix = getRowPrefix(type, row.id);
  const getField = (key: string) => editComponentsMap[`${rowPrefix}_${key}`];

  const invoicee = row._valuesCache?.invoicee;
  const isInvoice = invoicee === 'site' || invoicee === 'provider';

  return (
    <SiteValidationProvider
      showValidationErrors={showValidationErrors}
      province={province}
      setProvince={setProvince}
    >
      <Stack data-testid="create-site-modal" gap="lg">
        <Accordion
          multiple
          defaultValue={[
            'site-details',
            'billing-configuration',
            'payment-methods',
          ]}
          variant="separated"
          radius="md"
        >
          <ModalSection
            value="site-details"
            title="Site Details"
            description="Core site and location information."
            icon={<IconBuildings size={18} />}
            columns={2}
          >
            {[
              getField('name'),
              getField('organizationID'),
              getField('address'),
              getField('region'),
              getField('city'),
              getField('province'),
              getField('timezone'),
              getField('postalCode'),
              getField('email'),
              getField('phone'),
              getField('fax'),
              getField('status'),
              getField('siteNumber'),
              getField('hospitalCode'),
              getField('description'),
              getField('defaultPayorID'),
            ]}
          </ModalSection>

          <ModalSection
            value="billing-configuration"
            title="Billing Configuration"
            description="Invoice ownership, billing rates, and fee behavior."
            icon={<IconReceipt2 size={18} />}
            columns={isInvoice ? 3 : 2}
          >
            {[
              getField('invoicee'),
              getField('invoiceRate'),
              getField('overhead'),
              getField('proFees'),
              getField('techFees'),
              getField('allowUploads'),
            ]}
          </ModalSection>

          <ModalSection
            value="payment-methods"
            title="Payment Methods"
            description="Patient-facing payment options and their instructions."
            icon={<IconCreditCard size={18} />}
            columns={3}
          >
            {[
              getField('etransfer'),
              getField('creditCard'),
              getField('cheque'),
            ]}
          </ModalSection>

          <ModalSection
            value="integrations"
            title="Integrations"
            description="Optional clinic integration credentials."
            icon={<IconDeviceDesktopAnalytics size={18} />}
            columns={3}
          >
            {[getField('apiID'), getField('apiSecret'), getField('apiKey')]}
          </ModalSection>
        </Accordion>

        <Flex justify="center" gap="lg" mt="sm">
          <Button
            variant="outline"
            radius="md"
            color="gray"
            c={buttonTextColor(computedColorScheme, false)}
            onClick={() =>
              type === 'edit'
                ? table.setEditingRow(null)
                : table.setCreatingRow(null)
            }
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="outline"
            radius="md"
            color="green"
            c={buttonTextColor(computedColorScheme, false)}
            onClick={() => {
              void onSave();
            }}
            leftSection={<IconCirclePlus />}
            data-testid={`${type}-site-submit`}
          >
            Save
          </Button>
        </Flex>
      </Stack>
    </SiteValidationProvider>
  );
};
